import { measure } from '@/components/Analytics/Analytics';
import CollectionCard from '@/components/Collections/CollectionCard/CollectionCard';
import SectionBackground from '@/components/SectionBackground/SectionBackground';
import useCalendar from '@/hooks/calendar/useCalendar';
import useCards from '@/hooks/cards/useCards';
import { fullCollectionLength } from '@/hooks/collections/useCollections';
import useSpecies from '@/hooks/species/useSpecies';
import { CollectionAttributes, StrapiEntry } from '@/lib/apiTypes';
import cn from '@/lib/cs';
import { useInView } from 'framer-motion';
import { useMemo, useRef } from 'react';

const Collection = ({
	collection,
}: {
	collection: StrapiEntry<CollectionAttributes>;
}) => {
	const selfRef = useRef<HTMLLIElement>(null);
	const isInView = useInView(selfRef, {
		once: true,
		margin: '50% 50% 50% 50%',
	});

	const { getCardBySpeciesId } = useCards();
	const { getIsSpeciesInCalendar } = useCalendar();
	const { findSpeciesById } = useSpecies();

	// Get the cards of the collection, padded with undefined if there are less than a full collection's length
	const collectionCards = useMemo(() => {
		return (
			// Create an array of four entries
			Array(fullCollectionLength)
				// Fill with undefined
				.fill(undefined)
				// Replace with the species entries when they exist
				.map(
					(_, index) =>
						collection.attributes.speciesEntries.data?.[index] ??
						undefined,
				)
				// Put species that are included in the calendar first
				.sort(
					(a, b) =>
						Number(getIsSpeciesInCalendar(b?.id ?? 0)) -
						Number(getIsSpeciesInCalendar(a?.id ?? 0)),
				)
				// Put collected cards in front
				.sort(
					(a, b) =>
						Number(!!getCardBySpeciesId(b?.id ?? 0)) -
						Number(!!getCardBySpeciesId(a?.id ?? 0)),
				)
		);
	}, [
		collection.attributes.speciesEntries.data,
		getCardBySpeciesId,
		getIsSpeciesInCalendar,
	]);

	// Get the number of collected cards
	const numberOfCollectedCards = useMemo(
		() =>
			collectionCards.filter((card) => getCardBySpeciesId(card?.id ?? 0))
				.length,
		[collectionCards, getCardBySpeciesId],
	);

	// Has user collected any cards?
	const hasCollectedAnyCards = useMemo(
		() => numberOfCollectedCards > 0,
		[numberOfCollectedCards],
	);

	// Has user collected all cards?
	const hasCollectedAllCards = useMemo(
		() => numberOfCollectedCards === collectionCards.length,
		[collectionCards.length, numberOfCollectedCards],
	);

	return (
		<li
			ref={selfRef}
			className={cn(
				'transition-all duration-300 ease-out origin-top',
				isInView
					? 'opacity-100 scale-y-100'
					: 'opacity-0 motion-safe:scale-y-75',
			)}>
			<section
				className={cn(
					'relative text-white',
					hasCollectedAllCards && 'text-accentOrCompatLightest',
				)}>
				<div className="absolute inset-0 size-full pb-6 md:pb-10">
					<div className="relative size-full">
						<SectionBackground
							className={cn(
								'text-secondary',
								hasCollectedAnyCards && 'text-primary',
								hasCollectedAllCards &&
									'text-accentOrCompatDarkest',
							)}
						/>
						{hasCollectedAllCards && (
							<SectionBackground className="text-secondary opacity-60" />
						)}
					</div>
				</div>
				<div className="relative mx-auto w-full max-w-[40rem] p-4 md:py-6">
					<h2 className="flex font-brand text-3xl lg:text-4xl">
						<span className="grow first-letter:text-4xl lg:first-letter:text-5xl">
							{collection.attributes.titleShort}{' '}
							<span className="sr-only">
								{numberOfCollectedCards} van de
								{collectionCards.length} verzameld
							</span>
						</span>
						<span
							className="inline-flex shrink-0 items-center gap-1"
							role="presentation"
							aria-hidden>
							{numberOfCollectedCards}
							<div className="h-4 w-0.5 rotate-12 rounded-full bg-current" />
							<span className="text-2xl">
								{collectionCards.length}
							</span>
						</span>
					</h2>
					<ul
						aria-label={`Kaarten van de ${collection.attributes.titleLong}`}
						className="relative grid aspect-[8/3] items-center justify-center pt-1"
						style={{
							gridTemplateColumns: `repeat(${collectionCards.length}, minmax(0, 1fr))`,
						}}>
						{isInView &&
							collectionCards.map((species, index) => (
								<CollectionCard
									key={index}
									index={index}
									cardsLengthForZIndexFix={
										collectionCards.length
									}
									speciesId={species?.id}
									target="details"
									onClick={() => {
										const fullSpecies = findSpeciesById(
											species?.id ?? 0,
										);
										if (!fullSpecies) return;
										measure(
											`collection-species-${collection.id}-${collection.attributes.slug}-${fullSpecies.id}-${fullSpecies.attributes.slug}`,
											'navigation',
										);
									}}
								/>
							))}
					</ul>
				</div>
			</section>
		</li>
	);
};

export default Collection;
